/**
 * Home's layout geometry.
 *
 * The rules mirror `ReportsPage/helpers/reportDefinition.ts`, which has run a
 * 12-column `react-grid-layout` canvas in production for months: same column
 * count, same per-type minimums, same free-slot search. Home gets its own
 * module rather than importing that one, because a report widget carries
 * report-only concerns (compare rows, collapse, duplication) that a dashboard
 * tile does not, and refactoring a mature surface mid-flight to share three
 * functions buys less than it risks.
 *
 * WHERE THIS IS STORED. `DashboardViewV4.layout`, a sibling of `widgets`.
 * `ezbuild_back` keeps `dashboard_config` in a free-form `JSONField` with no
 * serializer validation, and its own reader (`_widgets_from_view`) looks only
 * at `view.widgets` and `view.hidden` — so a new sibling key round-trips
 * untouched and cannot break widget resolution. It deliberately does NOT go
 * inside `widget.config`: the backend's `validate_config` drops unknown keys
 * there.
 */

export const HOME_GRID_COLUMNS = 12
export const HOME_GRID_ROW_HEIGHT = 36
export const HOME_GRID_MARGIN: [number, number] = [16, 16]

export interface HomeLayoutItem {
  /** The widget's slug. `i` is react-grid-layout's own field name. */
  i: string
  x: number
  y: number
  w: number
  h: number
}

/** The four widths the spec offers, on a 12-column grid. */
export const WIDTH_PRESETS = {
  third: 4,
  half: 6,
  twoThirds: 8,
  full: 12
} as const

export type WidthPreset = keyof typeof WIDTH_PRESETS

const DEFAULT_MIN = { w: 3, h: 4 }

/**
 * The narrowest each kind stays readable at. A chart below four columns loses
 * its axis labels; a table loses every column but the first.
 */
const MIN_SIZE: Record<string, { w: number; h: number }> = {
  kpi: { w: 3, h: 3 },
  progress: { w: 3, h: 3 },
  chart: { w: 4, h: 5 },
  snapshot: { w: 6, h: 5 },
  table: { w: 4, h: 5 },
  list: { w: 3, h: 5 },
  feed: { w: 3, h: 5 },
  alert: { w: 3, h: 5 },
  map: { w: 4, h: 6 },
  section: { w: 6, h: 5 }
}

export const minSizeFor = (type?: string): { w: number; h: number } =>
  (type && MIN_SIZE[type]) || DEFAULT_MIN

/** The size a widget gets when it first lands on the grid. */
const DEFAULT_SIZE: Record<string, { w: number; h: number }> = {
  kpi: { w: 3, h: 3 },
  chart: { w: 6, h: 6 },
  snapshot: { w: 8, h: 6 },
  table: { w: 6, h: 8 },
  list: { w: 4, h: 8 },
  feed: { w: 4, h: 8 },
  alert: { w: 4, h: 8 },
  map: { w: 6, h: 8 },
  section: { w: 12, h: 6 }
}

export const defaultSizeFor = (type?: string): { w: number; h: number } => {
  const base = (type && DEFAULT_SIZE[type]) || { w: 4, h: 6 }
  const min = minSizeFor(type)
  return { w: Math.max(base.w, min.w), h: Math.max(base.h, min.h) }
}

const overlaps = (a: HomeLayoutItem, b: HomeLayoutItem): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h

/**
 * The first gap a `w x h` tile fits in, scanning left to right then down.
 * Falls through to a fresh row below everything, so a tile is never dropped
 * on top of another and never silently discarded for want of room.
 */
export const findFreeSlot = (
  layout: HomeLayoutItem[],
  w: number,
  h: number,
  columns = HOME_GRID_COLUMNS
): { x: number; y: number } => {
  const bottom = layout.reduce((acc, item) => Math.max(acc, item.y + item.h), 0)
  for (let y = 0; y <= bottom; y += 1) {
    for (let x = 0; x + w <= columns; x += 1) {
      const candidate: HomeLayoutItem = { i: "?", x, y, w, h }
      if (!layout.some((item) => overlaps(candidate, item))) return { x, y }
    }
  }
  return { x: 0, y: bottom }
}

/**
 * Every shown widget gets a slot, keeping the ones it already had.
 *
 * `order` decides where a widget with no stored geometry lands, so a layout
 * saved before geometry existed opens in the arrangement its order described
 * rather than a scrambled one.
 */
export const ensureLayout = (
  order: string[],
  stored: HomeLayoutItem[],
  typeOf: (slug: string) => string | undefined
): HomeLayoutItem[] => {
  const byId = new Map(stored.map((item) => [item.i, item]))
  const next: HomeLayoutItem[] = []
  for (const slug of order) {
    const existing = byId.get(slug)
    if (existing) {
      next.push(existing)
      continue
    }
    const { w, h } = defaultSizeFor(typeOf(slug))
    next.push({ i: slug, ...findFreeSlot(next, w, h), w, h })
  }
  return next
}

/** Drops geometry for widgets that are no longer shown. */
export const pruneLayout = (
  layout: HomeLayoutItem[],
  shown: ReadonlySet<string>
): HomeLayoutItem[] => layout.filter((item) => shown.has(item.i))

/**
 * Applies a width preset, floored at the widget's own minimum so a "one
 * third" that would make a table unreadable stays as narrow as it may get
 * and no narrower.
 */
export const applyWidthPreset = (
  layout: HomeLayoutItem[],
  slug: string,
  preset: WidthPreset,
  type?: string
): HomeLayoutItem[] => {
  const min = minSizeFor(type)
  const w = Math.max(WIDTH_PRESETS[preset], min.w)
  return layout.map((item) =>
    item.i === slug
      ? { ...item, w, x: Math.min(item.x, HOME_GRID_COLUMNS - w) }
      : item
  )
}

/** Which preset a width currently reads as, for marking the active choice. */
export const presetOf = (w: number): WidthPreset | null => {
  const found = (Object.keys(WIDTH_PRESETS) as WidthPreset[]).find(
    (key) => WIDTH_PRESETS[key] === w
  )
  return found ?? null
}

export type MoveDirection = "left" | "right" | "up" | "down"

/**
 * The keyboard alternative to dragging. One grid step per press, clamped to
 * the canvas; the grid's own compaction resolves whatever it collides with,
 * exactly as it does for a pointer drag.
 */
export const moveWidget = (
  layout: HomeLayoutItem[],
  slug: string,
  direction: MoveDirection
): HomeLayoutItem[] =>
  layout.map((item) => {
    if (item.i !== slug) return item
    if (direction === "left") return { ...item, x: Math.max(0, item.x - 1) }
    if (direction === "right")
      return { ...item, x: Math.min(HOME_GRID_COLUMNS - item.w, item.x + 1) }
    if (direction === "up") return { ...item, y: Math.max(0, item.y - 1) }
    return { ...item, y: item.y + 1 }
  })

/** react-grid-layout hands back its own items; keep only what we store. */
export const readLayout = (
  items: readonly { i: string; x: number; y: number; w: number; h: number }[]
): HomeLayoutItem[] => items.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }))
